import React from 'react';
import { useInterval, useWindowWidth, useComponentVisible, useClickOutside } from './hooks';
import { shareModal } from './share';
import Heading from 'components/common/Heading/Heading';
import Quote from 'components/stories/Body/Quote';
import { breakpointsInt, breakpoints } from 'themes/breakpoints.theme';

const helper = (() => {

    function isOnMobile (){
      return helper.useWindowWidth() <  breakpointsInt.sm;
    }

    function getEnvironment() {
      let hostName = window.location.hostname;
      if (hostName.indexOf('localhost') > -1 || hostName.indexOf('dev.movember.com') > -1){
        return 'local';
      } else if (hostName.indexOf('uat.movember.com') > -1){
        return 'uat';
      } else {
        return 'produs';
      }
    }

    function getUrl(url, language) {
      // Deprecating language specific
      // return (language !== 'en') ? `/${language + url}` : url;
      return url;
    }

    function getBaseUrl(country) {
      let baseCountry = country || "au";
      if (getEnvironment() === "local") {
        return `https://${baseCountry}.uat.movember.com/`;
      }
      return window.location.origin.concat("/");
    }

    function getAssetUrl(type, country, language) {
        let asset = null;
        let baseCountry = country || 'au';
        switch (type){
            default:
            case 'cms':
              if(getEnvironment() === 'local'){
                return `https://${baseCountry}.uat.movember.com/${language !== 'en' ? language + '/' : ''}sitemap.json`
              } else {
                return  (language !== 'en') ? `/${language}/sitemap.json` : '/sitemap.json';
              }
            case 'image':
              if(getEnvironment() === 'local'){
                // Temporary link to image urls on uat
                asset = `https://${baseCountry}.uat.movember.com/uploads/images/resources/`;
              } else {
                asset = '/uploads/images/resources/';
              }
        }
        return asset;
    }

    /**
     * This was built initially to be able to render images from the legacy site, given the relative path
     * to the image on the legacy site, which will be appended to the legacy website domain matching the current
     * environment.
     *
     * @todo - handle locales (ie. https://ca.movember.com/fr/IMAGE)
     * @todo - auto-detect country if not specifically passed?
     *
     * @param type
     * @param path
     * @param country
     * @returns {*}
     */
    function getLegacyAssetUrl(type, path, country)
    {
        let baseUrl = null;
        let baseCountry = country || "au"; // @todo, auto-detect default country?
        path = path.replace(/^\//, "");

        switch (type){
            case "image":
                switch (getEnvironment()) {
                    case "local":
                        baseUrl = `https://${baseCountry}.uat.movember.com/`;
                        break;
                    case "produs":
                        baseUrl = `https://${baseCountry}.movember.com/`;
                        break;
                    default:
                        baseUrl = `https://${baseCountry}.`+getEnvironment()+`.movember.com/`;
                        break;
                }
                break;
            case 'api':
                baseUrl = `https://${baseCountry + (getEnvironment() === 'produs' ? '' : '.uat')}.movember.com/api/v18/`;
                break;
            case 'json':
              if(getEnvironment() === 'local'){
                baseUrl = `https://${baseCountry}.uat.movember.com/`;
              } else {
                baseUrl = window.location.origin.concat('/');
              }
              break;
            default:
                baseUrl = window.location.origin.concat('/');
        }
        return baseUrl + path;
    }

    function getWordsPerMin(country) {
      switch (country) {
        case "au":
        case "us":
        case "uk":
        case "ca":
        case "nz":
          return 250;
        case "de":
          return 150;
        default:
          return 250;
      }
    }

    // Cookies get/set
    function setCookie(cname, cvalue, exdays, hasDomain) {
      let d = new Date();
      d.setTime(d.getTime() + (exdays*24*60*60*1000));
      let expires = "expires="+ d.toUTCString();
      let domain = hasDomain
        ? `domain=${
            getEnvironment() === "dev"
              ? ".localhost:3000"
              : getEnvironment() === "uat"
              ? ".uat.movember.com;"
              : ".movember.com;"
          }`
        : "";
      document.cookie = cname + "=" + cvalue + ";" + domain + expires + ";path=/";
    }

    function getCookie(cname) {
      var name = cname + "=";
      var decodedCookie = decodeURIComponent(document.cookie);
      var ca = decodedCookie.split(';');
      for(var i = 0; i <ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
      return "";
    }

    function getUserCookieName(postfix){
      return `movember-${getEnvironment()}-2016_${postfix}`;
    }

    function renderParagraph(content, quote, quotePosition) {
        const createText = data => {
          return data.map(item => {
              let element = item.text;
              // Link
              if(item.href){
                  element = (React.createElement('a', {href: item.href, target: '_blank'}, element));
              }
              // Styles
              if(item.marks && item.marks.length > 0){
                  item.marks.forEach(mark => {
                      if(mark === 'strong' || mark === 'em'){
                          element = (React.createElement(mark, null, element));
                      }
                  })
              }
              return element;
          });
      };
      let result = [];
        /* This contentCount is needed to show quote in the right position
        ** ol/ul items are combined in a paragrahp sometimes. */
        let contentCount = 0;
         // eslint-disable-next-line
        content.map((paragraph, i) => {
            // Content
            let tag;
            switch(paragraph.style){
                case 'h1':
                    tag = {name: 'Heading', level: 'h4'};
                    break;
                case 'ul':
                case 'ol':
                    tag = {name: paragraph.style};
                    break;
                default:
                    tag = {name: 'p'};
                    break;
            }   

            // If the data element isn't an array, make it one
            if (!Array.isArray(paragraph.data)) {
                paragraph.data = [paragraph.data];
            }

            // In case of rendering ordered list (ol) or unordered list (ul)
            // Have to create ol/ul element with li childs so it could display correct order numbers
            // Have to check next comming elements are still in ol/ul tag
            if(tag.name === 'ol' || tag.name === 'ul'){
                const currentListType = tag.name;
                // Do not render if this list item is already rendered in previous element.. i.e..previous element is also list item 
                if(content[i-1] && content[i-1].style === currentListType){
                     // eslint-disable-next-line
                    return;
                }
                //Check next element
                let li = [React.createElement('li', null, createText(content[i].data))];
                let index = i+1;
                while(content[index] && content[index].style === currentListType){
                    li.push(React.createElement('li', null, createText(content[index].data)));
                    index++;
                    contentCount++;
                }
                result.push(React.createElement(currentListType, {key: i}, li));
            } else {
                // Other Tags
                let currentElement = React.createElement(
                    tag.name === 'Heading' ? Heading : tag.name, 
                    tag.level ? { key: i, level: tag.level } : { key: i },
                    createText(paragraph.data)
                );
                result.push(currentElement);
            }

            // Quote
            if(quote){
                if(quotePosition && (quotePosition === contentCount + 1)){
                    result.push(<Quote content={quote} key={i}/>);
                }
                if(!quotePosition && (contentCount === content.length - 1)){
                    result.push(<Quote content={quote} key={i}/>);
                }
            }

            contentCount++;
        });

        return result;
    }
    
    function renderImage(image, size) {
      return image ? <picture>
            <source media={`(min-width:${breakpoints.md})`} srcSet={getImage(image, size === 'small' ? 'medium' : 'large')} type="image/webp"/>
            <source media={`(min-width:${breakpoints.md})`} srcSet={getImage(image, size === 'small' ? 'medium' : 'large', true)} type={getImageFormat(image)}/>
            <source media={`(min-width:${breakpoints.sm})`} srcSet={getImage(image, 'medium')} type="image/webp"/>
            <source media={`(min-width:${breakpoints.sm})`} srcSet={getImage(image, 'medium', true)} type={getImageFormat(image)}/>
            <source media={`(min-width:${breakpoints.xs})`} srcSet={getImage(image, 'small')} type="image/webp"/>
            <source media={`(min-width:${breakpoints.xs})`} srcSet={getImage(image, 'small', true)} type={getImageFormat(image)}/>
            <img src={helper.getImage(image, 'original', true)} alt={(image && image.alt) ? image.alt : 'no-alt'} />
        </picture> : null;
    }

    function getImage(item, size, isOriginal){
        const prefix = helper.getAssetUrl('image');
        let postfix = "";
        if(item && item.id && item.sizes && item.sizes.length > 0 && item.formats && item.formats.length > 0){
          let formatIndex = item.formats.indexOf('webp') >= 0 ? item.formats.indexOf('webp') : 0;
          if(isOriginal){
            postfix = `${item.id}-org.${item.formats[formatIndex]}`
          } else {
            let sizeIndex = item.sizes.indexOf(size) >= 0 ? item.sizes.indexOf(size) : 0;
            postfix = `${item.id}-${item.sizes[sizeIndex]}.${item.formats[formatIndex]}`;
          }
        } else {
            return item ? item.url : null;
        }
        return prefix + postfix;
    }

    function getImageFormat(item){
      let value = '';
      if(item && item.urls && item.urls.original && item.urls.original.original){
        let dotIndex = item.urls.original.original.lastIndexOf('.');
        value = "image/" + item.urls.original.original.substring(dotIndex + 1);
      }
      return value;
    }

    function getUrlLocale(){
      let locale = {
        country: "au",
        language: "en"
      }
      let {pathname, hostname} = window.location;
      //Check if url cointains country and language code (ex: ca.movember.com/fr)
      if(hostname.charAt(2) === '.'){
        locale.country = hostname.substr(0,2);
      }
      if(pathname.charAt(3) === '/'){
        locale.language = pathname.substr(1,2);
      }
      return locale;
    }

    /**
    *
    * getUrlParam
    *
    * @param paramId the parameter name
    * @param additionalAllowedCharacters additional characters that we consider valid when looking for a parameter value in the URL.
    *                                    As a general rule, only letters, numbers, '_', '-', '+', '%' and '.' are valid.
    *                                    If we want to consider any other valid characters (e.g. '@'), we have to include them here.
    */
   function getUrlParam(paramId, additionalAllowedCharacters) {
       var _chars = additionalAllowedCharacters || '';

       // Get the parameter string; note: for security reasons only certain characters are matched
        // eslint-disable-next-line
       var paramString = window.location.href.match( new RegExp(paramId + '=[-a-zA-Z0-9' + _chars + '_+%\.]*', 'g') );   // '_', '+', '%' and '.' characters added
       // Return the result:
       return ( paramString ? paramString[0].replace(paramId+'=','') : null );
   }

   function getDateFormat (date, country){
       switch (country) {
         case "us":
         case "ca":
           return date;
         default:
           break;
       }
    return date;
   }

   function nestedPropExists(obj, level1, ...deeperLevels) {
     if (obj === undefined) return false;
     if (
       deeperLevels.length === 0 &&
       Object.prototype.hasOwnProperty.call(obj, level1)
     )
       return true;
     return nestedPropExists(obj[level1], ...deeperLevels);
   }

   const copyToClipBoard = value => {
     if (navigator.userAgent.indexOf("Firefox") >= 0) {
       navigator.clipboard.writeText(value);
     } else {
       if (!document.queryCommandSupported || !document.queryCommandSupported("copy")) {
         return;
       }
       var clipboardContainer = document.getElementById("clipboard-container");
       setTimeout(function () {
         let input = document.createElement("input");
         input.setAttribute("readonly", "readonly");
         input.setAttribute("value", value);
         clipboardContainer.appendChild(input);

         input.select();
         input.setSelectionRange(0, input.value.length);

         document.execCommand("copy");
         clipboardContainer.removeChild(input);
       }, 500);
     }
   }; 
    
    return {
      useInterval: useInterval,
      useWindowWidth: useWindowWidth,
      useComponentVisible: useComponentVisible,
      useClickOutside: useClickOutside,
      shareModal: shareModal,
      isOnMobile: isOnMobile,
      getUrl: getUrl,
      getEnvironment: getEnvironment,
      nestedPropExists: nestedPropExists,
      setCookie: setCookie,
      getCookie: getCookie,
      getUserCookieName: getUserCookieName,
      renderParagraph: renderParagraph,
      renderImage: renderImage,
      getUrlLocale: getUrlLocale,
      getBaseUrl: getBaseUrl,
      getAssetUrl: getAssetUrl,
      getLegacyAssetUrl: getLegacyAssetUrl,
      getUrlParam: getUrlParam,
      getDateFormat: getDateFormat,
      getWordsPerMin: getWordsPerMin,
      getImage: getImage,
      copyToClipBoard: copyToClipBoard
    };
})();
 
export default helper;
